import styled from '@emotion/styled/macro';
import { forTabletPortraitDown, Variables } from '../base';

export const DashboardShell = styled.div`
    --accent-ring: ${Variables.ThemeLight.PrimaryOpacity2};
    --text-strong: ${Variables.ThemeLight.TextStrong};
    --text: ${Variables.ThemeLight.TextMedium};
    --text-med: ${Variables.ThemeLight.TextMedium};
    --text-muted: ${Variables.ThemeLight.TextMild};
    --text-mild: ${Variables.ThemeLight.TextMild};
    --text-faint: ${Variables.ThemeLight.TextMild};
    --accent: ${Variables.ThemeLight.PrimaryConstant6};
    --accent-hover: ${Variables.ThemeLight.PrimaryInverted5};
    --accent-selected-bg: ${Variables.ThemeLight.PrimaryMild2};
    --surface: ${Variables.ThemeLight.Background3};
    --surface-alt: ${Variables.ThemeLight.Background2};
    --surface-hover: ${Variables.ThemeLight.Background1};
    --surface-hover-2: ${Variables.ThemeLight.Background1};
    --surface-muted: ${Variables.ThemeLight.Background2};
    --border: ${Variables.ThemeLight.Border1};
    --border-soft: ${Variables.ThemeLight.NeutralMild3};
    --border-input: ${Variables.ThemeLight.Border1};
    --border-neutral: ${Variables.ThemeLight.Border1};
    --good: ${Variables.ThemeLight.SuccessInverted3};
    --good-strong: ${Variables.ThemeLight.SuccessConstant6};
    --good-bg: ${Variables.ThemeLight.SuccessMild3};
    --good-border: ${Variables.ThemeLight.SuccessMedium3};
    --bad: ${Variables.ThemeLight.WarningInverted3};
    --bad-bg: ${Variables.ThemeLight.WarningMild3};
    --bad-bg-soft: ${Variables.ThemeLight.WarningConstant11};
    --bad-border: ${Variables.ThemeLight.WarningMedium3};
    --warn: ${Variables.ThemeLight.AttentionInverted3};
    --warn-bg: ${Variables.ThemeLight.AttentionMild3};
    --warn-border: ${Variables.ThemeLight.AttentionMedium3};
    --neutral-bg: ${Variables.ThemeLight.Background1};
    --info-bg: ${Variables.ThemeLight.PrimaryMild3};
    --info-border: ${Variables.ThemeLight.PrimaryMedium3};
    --dot: ${Variables.ThemeLight.NeutralMedium1};

    color: var(--text);
    font-size: 14px;
    line-height: 1.4;

    * {
        box-sizing: border-box;
    }

    a {
        color: var(--accent);
        text-decoration: none;
    }

    a:hover {
        color: var(--accent-hover);
    }

    .page {
        display: flex;
        flex-direction: column;
        gap: 16px;
        min-width: 0;
    }

    .page-head {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: 24px;
    }

    .page-head-text {
        display: flex;
        flex-direction: column;
        gap: 4px;
        min-width: 0;
    }

    .page-head-text p {
        margin: 0;
        max-width: 640px;
        color: var(--text-mild);
        font-size: 13px;
        line-height: 1.5;
    }

    .page-head-actions {
        display: flex;
        flex: 0 0 auto;
        gap: 8px;
    }

    h1 {
        margin: 0;
        color: var(--text-strong);
        font-size: 24px;
        font-weight: 600;
        line-height: 1.2;
    }

    h2 {
        margin: 0;
        color: var(--text-strong);
        font-size: 20px;
        font-weight: 600;
        line-height: 1.3;
    }

    /*
     * The two buttons that are not a stock BaseButton look: the rule list rows and the status filter
     * pills are BaseButton's unstyled variant, which centres and resets everything, so what a
     * <button> here used to inherit is stated. Primary, outlined, input, banner and notice styling
     * comes from the base components — no element rules above them, which would out-rank those.
     */
    .rule-row,
    .status-filter-pill {
        border-radius: 6px;
        cursor: pointer;
        font-family: inherit;
        font-size: 13px;
        font-weight: 600;
        white-space: nowrap;
    }

    .rule-row:focus-visible,
    .status-filter-pill:focus-visible {
        outline: 2px solid var(--accent);
        outline-offset: 2px;
    }

    .stat-row {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(min(170px, 100%), 1fr));
        gap: 12px;
    }

    .stat {
        display: flex;
        flex-direction: column;
        gap: 2px;
        padding: 14px 16px;
        border: 1px solid var(--border);
        border-radius: 8px;
        background: var(--surface);
    }

    .stat-label {
        color: var(--text-mild);
        font-size: 12px;
    }

    .stat-value {
        color: var(--text-strong);
        font-size: 24px;
        font-weight: 600;
        line-height: 1.2;
        font-variant-numeric: tabular-nums;
    }

    .stat-value.date {
        font-size: 16px;
    }

    .stat-note {
        color: var(--text-faint);
        font-size: 12px;
    }

    .split {
        display: grid;
        grid-template-columns: minmax(0, 340px) minmax(0, 1fr);
        gap: 16px;
        align-items: start;
        min-width: 0;
    }

    .rules-pane {
        border: 1px solid var(--border);
        border-radius: 8px;
        background: var(--surface);
        overflow: hidden;
    }

    .rules-pane-head {
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding: 12px 14px;
        border-bottom: 1px solid var(--border);
    }

    .rules-pane-title {
        display: flex;
        align-items: center;
        justify-content: space-between;
        color: var(--text-strong);
        font-size: 14px;
        font-weight: 600;
    }

    .rules-pane-title span:last-child {
        color: var(--text-faint);
        font-size: 12px;
        font-weight: 400;
    }

    .rules-list {
        max-height: 640px;
        overflow-y: auto;
    }

    .rule-row {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: flex-start;
        gap: 4px;
        width: 100%;
        padding: 12px 14px;
        border: 0;
        border-bottom: 1px solid var(--border-soft);
        border-left: 2px solid transparent;
        background: var(--surface);
        cursor: pointer;
        text-align: left;
    }

    .rule-row:hover {
        background: var(--surface-hover);
    }

    .rule-row.selected {
        background: var(--accent-selected-bg);
        border-left-color: var(--accent);
    }

    .rule-row-top {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .rule-row-label {
        color: var(--text-strong);
        font-size: 13px;
        font-weight: 600;
        white-space: nowrap;
    }

    .rule-row-claims {
        margin-left: auto;
        color: var(--text-mild);
        font-size: 12px;
        font-variant-numeric: tabular-nums;
    }

    .rule-row-desc {
        color: var(--text);
        font-size: 13px;
        line-height: 1.35;
    }

    .rule-row-meta {
        color: var(--text-faint);
        font-size: 12px;
    }

    .rule-row-failed {
        color: var(--bad);
        font-weight: 600;
    }

    .methodology-note {
        margin: 0;
        color: var(--text-faint);
        font-size: 12px;
        line-height: 1.5;
    }

    .rules-empty {
        padding: 24px 14px;
        color: var(--text-faint);
        font-size: 13px;
        text-align: center;
    }

    .detail-pane {
        display: flex;
        flex-direction: column;
        gap: 16px;
        min-width: 0;
        padding: 20px 24px;
        border: 1px solid var(--border);
        border-radius: 8px;
        background: var(--surface);
    }

    .detail-empty {
        padding: 56px 24px;
        color: var(--text-faint);
        font-size: 14px;
        text-align: center;
    }

    .detail-head {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 24px;
    }

    .detail-head-text {
        display: flex;
        flex-direction: column;
        gap: 6px;
        min-width: 0;
    }

    .detail-head-text p {
        margin: 0;
        color: var(--text-mild);
        font-size: 13px;
    }

    .detail-badges {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 10px;
    }

    .detail-eyebrow {
        color: var(--text-mild);
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.04em;
    }

    .detail-slug {
        color: var(--text-faint);
        font-size: 12px;
        font-family: 'SFMono-Regular', ui-monospace, Menlo, Consolas, monospace;
    }

    .metric-strip {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(min(150px, 100%), 1fr));
        gap: 1px;
        border: 1px solid var(--border);
        border-radius: 8px;
        background: var(--border);
        overflow: hidden;
    }

    .metric {
        display: flex;
        flex-direction: column;
        gap: 2px;
        padding: 12px 14px;
        background: var(--surface);
    }

    .metric-label {
        color: var(--text-mild);
        font-size: 12px;
    }

    .metric-value {
        color: var(--text-strong);
        font-size: 17px;
        font-weight: 600;
        font-variant-numeric: tabular-nums;
    }

    .metric-value.good {
        color: var(--good);
    }

    .metric-value.bad {
        color: var(--bad);
    }

    .metric-note {
        color: var(--text-faint);
        font-size: 12px;
    }

    .claims-block {
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding-top: 16px;
        border-top: 1px solid var(--border-soft);
    }

    .claims-head-row {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 12px;
    }

    .claims-head {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    .claims-head strong {
        color: var(--text-strong);
        font-size: 14px;
        font-weight: 600;
    }

    .claims-head > span {
        color: var(--text-faint);
        font-size: 12px;
    }

    .filter-bar {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px;
    }

    .filter-bar-label {
        margin-right: 2px;
        color: var(--text-mild);
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.04em;
        text-transform: uppercase;
    }

    .claims-table {
        border: 1px solid var(--border);
        border-radius: 8px;
        overflow-x: auto;
    }

    .claims-thead,
    .claims-row {
        display: grid;
        grid-template-columns: 120px 140px 140px 140px minmax(180px, 1fr);
        align-items: center;
        min-width: 720px;
    }

    .claims-thead {
        background: var(--surface-hover);
        border-bottom: 1px solid var(--border);
    }

    .claims-thead span {
        padding: 10px 14px;
        color: var(--text);
        font-size: 12px;
        font-weight: 600;
    }

    .claims-row {
        border-bottom: 1px solid var(--border-soft);
    }

    .claims-row:last-child {
        border-bottom: 0;
    }

    .claims-row:hover {
        background: var(--surface-hover);
    }

    .claims-row > span {
        padding: 11px 14px;
        min-width: 0;
        color: var(--text-med);
        font-size: 13px;
    }

    .claims-row .col-claim,
    .claims-row .col-claim a {
        font-weight: 500;
        font-variant-numeric: tabular-nums;
    }

    .claims-row .col-msg {
        color: var(--text-mild);
        overflow-wrap: anywhere;
    }

    .claims-more {
        display: flex;
        justify-content: center;
        margin-top: 8px;
    }

    .claims-empty {
        padding: 20px;
        border: 1px dashed var(--border);
        border-radius: 8px;
        color: var(--text-faint);
        font-size: 13px;
        text-align: center;
    }

    .status-pill {
        display: inline-flex;
        align-items: center;
        height: 20px;
        padding: 0 8px;
        border: 1px solid transparent;
        border-radius: 4px;
        background: var(--neutral-bg);
        color: var(--text-muted);
        font-size: 10px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
    }

    .status-success,
    .status-completed {
        background: var(--good-bg);
        color: var(--good);
        border-color: var(--good-border);
    }

    .status-failed {
        background: var(--bad-bg);
        color: var(--bad);
        border-color: var(--bad-border);
    }

    .status-missing,
    .status-pending {
        background: var(--warn-bg);
        color: var(--warn);
        border-color: var(--warn-border);
    }

    .status-submitted {
        background: var(--neutral-bg);
        color: var(--text-muted);
        border-color: var(--border-neutral);
    }

    button.status-filter-pill {
        height: 26px;
        padding: 0 10px;
        border: 1px solid transparent;
        opacity: 0.55;
    }

    button.status-filter-pill.selected {
        border-color: currentColor;
        opacity: 1;
    }

    .status-filter-count {
        margin-left: 6px;
        font-size: 10px;
        opacity: 0.75;
    }

    .connection-band {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(220px, 280px);
        gap: 20px;
        padding: 20px;
        border: 1px solid var(--border);
        border-radius: 8px;
        background: var(--surface);
    }

    .connection-form {
        display: grid;
        grid-template-columns: repeat(4, minmax(160px, 1fr));
        gap: 16px;
        align-items: end;
    }

    .connection-state {
        display: flex;
        gap: 12px;
        align-items: center;
        padding: 14px;
        border: 1px solid var(--border);
        border-radius: 8px;
        background: var(--surface-alt);
    }

    .connection-state strong {
        display: block;
        color: var(--text-strong);
        font-size: 14px;
    }

    .connection-state span {
        color: var(--text-mild);
        font-size: 12px;
    }

    .state-dot {
        width: 10px;
        height: 10px;
        flex: 0 0 10px;
        border-radius: 50%;
        background: var(--dot);
    }

    .state-dot.ready {
        background: var(--good-strong);
    }

    .global-rules-section {
        display: flex;
        flex-direction: column;
        gap: 12px;
        min-width: 0;
        padding: 20px 24px;
        border: 1px solid var(--border);
        border-radius: 8px;
        background: var(--surface);
    }

    .global-rules-head {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    .global-rules-head strong {
        color: var(--text-strong);
        font-size: 16px;
        font-weight: 600;
    }

    .global-rules-head > span {
        color: var(--text-faint);
        font-size: 13px;
    }

    .global-rules-table {
        border: 1px solid var(--border);
        border-radius: 8px;
        overflow-x: auto;
    }

    .global-rules-thead,
    .global-rules-row {
        display: grid;
        grid-template-columns: 200px minmax(280px, 1fr) 100px;
        align-items: center;
        min-width: 580px;
    }

    .global-rules-thead {
        background: var(--surface-hover);
        border-bottom: 1px solid var(--border);
    }

    .global-rules-thead span {
        padding: 10px 14px;
        color: var(--text);
        font-size: 12px;
        font-weight: 600;
    }

    .global-rules-row {
        border-bottom: 1px solid var(--border-soft);
    }

    .global-rules-row:last-child {
        border-bottom: 0;
    }

    .global-rules-row > span {
        padding: 11px 14px;
        min-width: 0;
        color: var(--text-med);
        font-size: 13px;
    }

    .global-rules-row .col-name {
        font-weight: 600;
        color: var(--text-strong);
    }

    ${forTabletPortraitDown(`
    .page-head {
      flex-direction: column;
      align-items: stretch;
    }

    .connection-band,
    .connection-form {
      grid-template-columns: 1fr;
    }

    h1 {
      font-size: 22px;
    }
  `)}

    @media (max-width: 720px) {
        .split {
            grid-template-columns: 1fr;
        }

        .rules-list {
            max-height: 340px;
        }
    }

    @media (max-width: 480px) {
        .page-head-text p {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
        }
    }
`;
